import * as i0 from '@angular/core';
import { Injectable, Component, Inject, Input, NgModule } from '@angular/core';
import { Subject, fromEvent } from 'rxjs';
import * as i2 from '@angular/common';
import { DOCUMENT, CommonModule } from '@angular/common';
import { trigger, state, style, transition, animate } from '@angular/animations';

const HintConfig = {
    HINT_TAG: 'tour-step',
    DEFAULT_POSITION: 'bottom',
    Z_INDEX: '999',
    DEFAULT_PX_LAYER: 15,
    APPLY_RELATIVE: true,
    DISMISS_ON_OVERLAY: true,
    HAS_CLOSE_BUTTON: true,
};

class HintOptions {
    constructor() {
        this.elementsDisabled = true;
        this.defaultPosition = HintConfig.DEFAULT_POSITION;
        this.defaultLayer = HintConfig.DEFAULT_PX_LAYER;
        this.applyRelative = HintConfig.APPLY_RELATIVE;
        this.stepTag = HintConfig.HINT_TAG;
        this.dismissOnOverlay = HintConfig.DISMISS_ON_OVERLAY;
        this.hasCloseButton = HintConfig.HAS_CLOSE_BUTTON;
    }
}

const NULL_STEP = { selector: HintConfig.HINT_TAG, order: -1 };
class NgxCustomTourService {
    constructor() {
        this.currentStep = NULL_STEP;
        this.steps = [];
        this.hintOptions = new HintOptions();
        this.anchors = {};
        this.registration$ = new Subject();
        this.finish$ = new Subject();
        this.showingStep$ = new Subject();
    }
    /**
     * Initialize hint service
     * @method initialize
     * @param  {HintOptions} options init options
     * @return void
     */
    initialize(options = new HintOptions()) {
        this.hintOptions = Object.assign(new HintOptions(), options);
        let nodes = document.getElementsByTagName(this.hintOptions.stepTag);
        this.steps = this.initSteps(nodes);
        this.startAt(0);
    }
    /**
     * Refresh highlighted elements
     * @method updateHighlightedElements
     * @return void
     */
    updateHighlightedElements() {
        const anchor = this.anchors[this.currentStep.order];
        anchor.updateHighlightedElements();
    }
    /**
     * Refresh hint position
     * @method setHintPosition
     * @return void
     */
    setHintPosition() {
        const anchor = this.anchors[this.currentStep.order];
        anchor.setHintPosition();
    }
    /**
     * Show step
     * @method show
     * @param  {Step} step [description]
     */
    show(step) {
        const anchor = this.anchors[step.order];
        if (!anchor) {
            return;
        }
        anchor.showStep();
    }
    /**
     * Show step next to {Step} this.currentStep
     * @method showNext
     */
    showNext() {
        if (!this.hasNext()) {
            this.end();
            return;
        }
        this.anchors[this.currentStep.order].hideStep();
        this.currentStep = this.steps[this.steps.indexOf(this.currentStep) + 1];
        const anchor = this.anchors[this.currentStep.order];
        anchor.showStep();
    }
    /**
     * Show step previous to {Step} this.currentStep
     * @method showPrev
     */
    showPrev() {
        this.anchors[this.currentStep.order].hideStep();
        this.currentStep = this.steps[this.steps.indexOf(this.currentStep) - 1];
        const anchor = this.anchors[this.currentStep.order];
        if (!anchor) {
            return;
        }
        anchor.showStep();
    }
    /**
     * Register hint component
     * @method register
     * @param  {string}            selector  binded to
     * @param  {TourComponent} component itself
     */
    register(component) {
        if (this.anchors[component.order]) {
            throw new Error(`Duplicated step orders. Step's order must be unique.`);
        }
        this.anchors[component.order] = component;
        this.registration$.next(true);
    }
    /**
     * Is {Step} this.currentStep has next
     * @method hasNext
     * @return {boolean}
     */
    hasNext() {
        return this.steps.indexOf(this.currentStep) < this.steps.length - 1;
    }
    /**
     * Is {Step} this.currentStep has previous
     * @method hasPrev
     * @return {boolean}
     */
    hasPrev() {
        return this.steps.indexOf(this.currentStep) > 0;
    }
    /**
     * Finalize our hint tour.
     * @method end
     */
    end() {
        const anchor = this.anchors[this.currentStep.order];
        if (!anchor) {
            return;
        }
        this.currentStep = NULL_STEP;
        anchor.hideStep();
        this.finish$.next(true);
    }
    /**
     * Start hint tour at some position
     * @method startAt
     * @param  {number} stepId position in this.steps
     */
    startAt(stepId) {
        this.currentStep = this.steps[stepId];
        this.show(this.currentStep);
    }
    /**
     * Convert Element[] to Step[]
     * @method initSteps
     * @param  {NodeListOf<Element>} nodes
     * @return {Step[]}
     */
    initSteps(nodes) {
        let steps = [];
        for (let i = 0; i < nodes.length; i++) {
            steps.push({
                selector: nodes[i].getAttribute('ng-reflect-selector') || this.getSelectorFromClassName('selector', nodes[i]) || '',
                order: parseFloat(nodes[i].getAttribute('ng-reflect-order') || this.getSelectorFromClassName('order', nodes[i]) || '') || i,
            });
        }
        return steps = steps.sort((el1, el2) => {
            return el1.order - el2.order;
        });
    }
    getSelectorFromClassName(selector, node) {
        let result;
        node.classList.forEach(klass => {
            if (klass.startsWith(`${selector}-`)) {
                result = klass.split(`${selector}-`)[1];
            }
        });
        return result;
    }
}
NgxCustomTourService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: NgxCustomTourService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
NgxCustomTourService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: NgxCustomTourService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: NgxCustomTourService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

const tourHintAnimation = trigger('tourHintAnimation', [
    state('*', style({
        webkitTransform: '{{ transformEnd }}',
        transform: '{{ transformEnd }}'
    }), { params: { transformStart: 'translateY(0)', transformEnd: 'translateY(0)' } }),
    transition(':enter', [
        style({
            opacity: 0,
            webkitTransform: '{{ transformStart }}',
            transform: '{{ transformStart }}',
        }),
        animate('.5s cubic-bezier(.4,.04,.08,1.42)', style({
            opacity: 1,
            webkitTransform: '{{ transformEnd }}',
            transform: '{{ transformEnd }}',
        })),
    ], { params: { transformStart: 'translateY(0)', transformEnd: 'translateY(0)' } }),
    transition(':leave', [
        style({
            opacity: 1,
            webkitTransform: '{{ transformEnd }}',
            transform: '{{ transformEnd }}',
        }),
        animate('.3s cubic-bezier(.25,.1,.25,1)', style({
            opacity: 0,
            webkitTransform: '{{ transformStart }}',
            transform: '{{ transformStart }}',
        })),
        animate('.2s ease', style({
            'max-height': '0px',
        })),
    ], { params: { transformStart: 'translateY(0)', transformEnd: 'translateY(0)' } }),
]);

var TourStepPosition;
(function (TourStepPosition) {
    TourStepPosition["Top"] = "top";
    TourStepPosition["Bottom"] = "bottom";
    TourStepPosition["Left"] = "left";
    TourStepPosition["Right"] = "right";
    TourStepPosition["Neutral"] = "neutral";
})(TourStepPosition || (TourStepPosition = {}));
const getPath = (elements) => {
    const { innerWidth, innerHeight } = window;
    const points = elements.map(element => {
        const { left, top, width, height } = element.getBoundingClientRect();
        const edges = {
            top: top,
            right: left + width,
            bottom: top + height,
            left: left,
        };
        const pointsPath = {
            leftTop: `M${edges.left},${edges.top} Q${edges.left},${edges.top} ${edges.left},${edges.top}`,
            rightTop: `V${edges.top} Q${edges.right},${edges.top} ${edges.right},${edges.top}`,
            rightBottom: `H${edges.right} Q${edges.right},${edges.bottom} ${edges.right},${edges.bottom}`,
            leftBottom: `V${edges.bottom} Q${edges.left},${edges.bottom} ${edges.left},${edges.bottom}`
        };
        return `
      ${pointsPath.leftTop}
      ${pointsPath.leftBottom}
      ${pointsPath.rightBottom}
      ${pointsPath.rightTop}
    `;
    });
    return `
    M${innerWidth},${innerHeight}
    H0V0
    H${innerWidth}V${innerHeight}
    Z
    ${points.join('Z')}
    Z
  `;
};
const setHintPosition = (position, elements, hintService, customCss) => {
    const highlightedElement = elements[0];
    if (highlightedElement) {
        switch (position) {
            case TourStepPosition.Top:
                return {
                    transformClass: 'transformX_50 transformY_100',
                    transformStart: 'translateX(-50%) translateY(-60%)',
                    transformEnd: 'translateX(-50%) translateY(-100%)',
                    topPos: highlightedElement.offsetTop - hintService.hintOptions.defaultLayer,
                    leftPos: highlightedElement.offsetLeft + highlightedElement.offsetWidth / 2,
                };
            case TourStepPosition.Bottom:
                return {
                    transformClass: 'transformX_50',
                    transformStart: 'translateX(-50%) translateY(-20%)',
                    transformEnd: 'translateX(-50%) translateY(0%)',
                    topPos: highlightedElement.offsetTop + highlightedElement.offsetHeight + hintService.hintOptions.defaultLayer,
                    leftPos: highlightedElement.offsetLeft + highlightedElement.offsetWidth / 2,
                };
            case TourStepPosition.Left:
                return {
                    transformClass: 'transformY_50 transformX_100',
                    transformStart: 'translateY(-50%) translateX(-75%)',
                    transformEnd: 'translateY(-50%) translateX(-100%)',
                    topPos: highlightedElement.offsetTop + highlightedElement.offsetHeight / 2,
                    leftPos: highlightedElement.offsetLeft - hintService.hintOptions.defaultLayer,
                };
            case TourStepPosition.Right:
                return {
                    transformClass: 'transformY_50',
                    transformStart: 'translateY(-50%) translateX(-10%)',
                    transformEnd: 'translateY(-50%) translateX(0%)',
                    topPos: highlightedElement.offsetTop + highlightedElement.offsetHeight / 2,
                    leftPos: highlightedElement.offsetLeft + highlightedElement.offsetWidth + hintService.hintOptions.defaultLayer,
                };
            case TourStepPosition.Neutral:
                return {
                    topPos: highlightedElement.offsetTop + highlightedElement.offsetHeight / 2,
                    leftPos: highlightedElement.offsetLeft + highlightedElement.offsetWidth + hintService.hintOptions.defaultLayer,
                    transformClass: customCss,
                };
            default:
                throw 'Invalid hint position ->' + position;
        }
    }
    else {
        return {
            transformClass: 'transformY_50 transformX_50',
            transformStart: 'translateY(50%) translateX(-50%)',
            transformEnd: 'translateY(-50%) translateX(-50%)',
            topPos: window.innerHeight / 2,
            leftPos: window.innerWidth / 2,
        };
    }
};

class TourComponent {
    constructor(hintService, document) {
        this.hintService = hintService;
        this.document = document;
        this.title = '';
        this.order = 0;
        this.position = TourStepPosition.Bottom;
        this.hasCloseButton = true;
        this.isFooterVisible = true;
        this.customCss = '';
        this.prevButtonText = '←';
        this.nextButtonText = '→';
        this.finishButtonText = 'Finish';
        this.showme = false;
        this.hasNext = false;
        this.hasPrev = false;
        this.topPos = 1;
        this.leftPos = 1;
        this.highlightedElements = [];
        this.transformClass = 'transformX_50 transformY_100';
        this.transformStart = 'translateX(-50%) translateY(-60%)';
        this.transformEnd = 'translateX(-50%) translateY(-100%)';
        this.transformY = false;
        this.transformX = false;
        this.path = '';
    }
    ngOnInit() {
        this.hintService.register(this);
        fromEvent(window, 'resize').subscribe({
            next: () => {
                if (this.showme) {
                    this.setHintPosition();
                    this.hightlightElements();
                }
            }
        });
        fromEvent(window, 'scroll').subscribe({
            next: () => {
                if (this.showme) {
                    this.hightlightElements();
                }
            }
        });
    }
    showStep() {
        this.getHighlightedElements();
        this.hintService.showingStep$.next(this);
        this.hasCloseButton = this.hasCloseButton || this.hintService.hintOptions.hasCloseButton;
        this.position = this.position || this.hintService.hintOptions.defaultPosition;
        this.order = +this.order;
        this.hightlightElements();
        const highlightedElement = this.highlightedElements[0];
        if (highlightedElement) {
            if (this.hintService.hintOptions.elementsDisabled) {
                this.disableClick(highlightedElement);
            }
            if (this.hintService.hintOptions.applyRelative) {
                highlightedElement.classList.add('hint-relative');
            }
        }
        this.setHintPosition();
        this.showme = true;
        this.hasNext = this.hintService.hasNext();
        this.hasPrev = this.hintService.hasPrev();
    }
    hideStep() {
        this.removeHightlighting();
        this.showme = false;
    }
    exit() {
        this.hintService.end();
    }
    next() {
        this.hintService.showNext();
    }
    prev() {
        this.hintService.showPrev();
    }
    disableClick(element) {
        element.classList.add('hint-disabled');
    }
    enableClick(element) {
        element.classList.remove('hint-disabled');
    }
    hightlightElements() {
        this.path = getPath(this.highlightedElements);
    }
    removeHightlighting() {
        this.highlightedElements.map(highlightedElement => {
            highlightedElement.style.zIndex = '0';
            this.enableClick(highlightedElement);
            highlightedElement.classList.remove('tour-hint-hightlight');
            highlightedElement.classList.remove('hint-relative');
        });
    }
    ngOnChanges(changes) {
        if (changes['selector']) {
            this.showme ? this.updateHighlightedElements() : this.getHighlightedElements();
        }
    }
    updateHighlightedElements() {
        this.removeHightlighting();
        this.showStep();
    }
    getHighlightedElements() {
        this.highlightedElements = [];
        switch (typeof this.selector) {
            case 'string':
                const elements = this.document.querySelectorAll(this.selector);
                elements.forEach(element => this.highlightedElements.push(element));
                break;
            case 'object':
                this.selector.map(selector => {
                    const elements = this.document.querySelectorAll(selector);
                    elements.forEach(element => this.highlightedElements.push(element));
                });
                break;
            default:
                break;
        }
    }
    setHintPosition() {
        const { transformClass, transformStart, transformEnd, topPos, leftPos } = setHintPosition(this.position, this.highlightedElements, this.hintService, this.customCss);
        this.transformClass = transformClass;
        this.transformStart = transformStart;
        this.transformEnd = transformEnd;
        this.topPos = topPos;
        this.leftPos = leftPos;
    }
}
TourComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: TourComponent, deps: [{ token: NgxCustomTourService }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Component });
TourComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.11", type: TourComponent, selector: "tour-step[order]", inputs: { title: "title", selector: "selector", order: "order", position: "position", hasCloseButton: "hasCloseButton", isFooterVisible: "isFooterVisible", customCss: "customCss", prevButtonText: "prevButtonText", nextButtonText: "nextButtonText", finishButtonText: "finishButtonText" }, usesOnChanges: true, ngImport: i0, template: `<div class="intro-tour-hint-wrapper {{transformClass}} step{{order}} {{position}}"
  *ngIf="showme" [ngStyle]="{'top': topPos+'px', 'left': leftPos+'px'}"
  [@tourHintAnimation]="{value: transformStart, params: { transformStart: transformStart, transformEnd: transformEnd}}">
    <div class="header" *ngIf="title">
        {{title}}
        <a *ngIf="hasCloseButton" class="header-btn__close scalable" (click)="exit()">&#10006;</a>
    </div>
    <div class="content"><ng-content></ng-content></div>
    <div class="footer" *ngIf="isFooterVisible">
      <a class="navigate-btn navigate-btn__previous scalable" *ngIf="hasPrev" (click)="prev()">{{prevButtonText}}</a>
      <a class="navigate-btn navigate-btn__next scalable" *ngIf="hasNext" (click)="next()">{{nextButtonText}}</a>
      <a class="navigate-btn navigate-btn__finish scalable" *ngIf="!hasNext" (click)="exit()">{{finishButtonText}}</a>
    </div>
  </div>
  <div
    *ngIf="showme"
    class="overlay"
    (click)="hintService.hintOptions.dismissOnOverlay ? exit() : next()"
    [ngStyle]="{ 'pointer-events': hintService.hintOptions.elementsDisabled ? 'auto' : 'none' }"
  >
    <svg>
      <svg:path [attr.d]="path"></svg:path>
    </svg>
  </div>`, isInline: true, directives: [{ type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }], animations: [tourHintAnimation] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: TourComponent, decorators: [{
            type: Component,
            args: [{
                    selector: `${HintConfig.HINT_TAG}[order]`,
                    template: `<div class="intro-tour-hint-wrapper {{transformClass}} step{{order}} {{position}}"
  *ngIf="showme" [ngStyle]="{'top': topPos+'px', 'left': leftPos+'px'}"
  [@tourHintAnimation]="{value: transformStart, params: { transformStart: transformStart, transformEnd: transformEnd}}">
    <div class="header" *ngIf="title">
        {{title}}
        <a *ngIf="hasCloseButton" class="header-btn__close scalable" (click)="exit()">&#10006;</a>
    </div>
    <div class="content"><ng-content></ng-content></div>
    <div class="footer" *ngIf="isFooterVisible">
      <a class="navigate-btn navigate-btn__previous scalable" *ngIf="hasPrev" (click)="prev()">{{prevButtonText}}</a>
      <a class="navigate-btn navigate-btn__next scalable" *ngIf="hasNext" (click)="next()">{{nextButtonText}}</a>
      <a class="navigate-btn navigate-btn__finish scalable" *ngIf="!hasNext" (click)="exit()">{{finishButtonText}}</a>
    </div>
  </div>
  <div
    *ngIf="showme"
    class="overlay"
    (click)="hintService.hintOptions.dismissOnOverlay ? exit() : next()"
    [ngStyle]="{ 'pointer-events': hintService.hintOptions.elementsDisabled ? 'auto' : 'none' }"
  >
    <svg>
      <svg:path [attr.d]="path"></svg:path>
    </svg>
  </div>`,
                    animations: [tourHintAnimation]
                }]
        }], ctorParameters: function () { return [{ type: NgxCustomTourService }, { type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; }, propDecorators: { title: [{
                type: Input
            }], selector: [{
                type: Input
            }], order: [{
                type: Input
            }], position: [{
                type: Input
            }], hasCloseButton: [{
                type: Input
            }], isFooterVisible: [{
                type: Input
            }], customCss: [{
                type: Input
            }], prevButtonText: [{
                type: Input
            }], nextButtonText: [{
                type: Input
            }], finishButtonText: [{
                type: Input
            }] } });

class NgxCustomTourModule {
}
NgxCustomTourModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: NgxCustomTourModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
NgxCustomTourModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: NgxCustomTourModule, declarations: [TourComponent], imports: [CommonModule], exports: [TourComponent] });
NgxCustomTourModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: NgxCustomTourModule, imports: [[
            CommonModule,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: NgxCustomTourModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        TourComponent
                    ],
                    imports: [
                        CommonModule,
                    ],
                    exports: [
                        TourComponent
                    ]
                }]
        }] });

/*
 * Public API Surface of ngx-custom-tour
 */

/**
 * Generated bundle index. Do not edit.
 */

export { NgxCustomTourModule, NgxCustomTourService, TourComponent };
